import React, { useCallback, useMemo, useRef, useState } from "react";
import { Image } from "../../../client/api";
import { calcMediaStyle } from "../../../config/mediaUtils";

export declare type CloudflareFormat = 'thumb' | 'icon' | 'full' | 'zoomable';
const ClavaImage: React.FC<{
  image?: Image;
  imageUrl?: string;
  format?: CloudflareFormat;
  width: number | string;
  className?: string;
  isEditable?: boolean;
  alt?: string;
  forceAspectRatio?: number;
  rounded?: boolean;
  round?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
}> = ({ format, image, imageUrl, width, isEditable, className, alt, forceAspectRatio, rounded, round, objectFit }) => {
  const outerDiv = useRef<HTMLDivElement>(null);
  const cloudflareWidth = useMemo(() => {
    const f = format ?? 'full';
    return f === 'thumb' ? 128 : f === 'icon' ? 512 : f === 'full' ? 1080 : 1920;
  }, [format]);
  const [reload, setReload] = useState(128);
  const divWidth = useMemo(() => {
    if (outerDiv.current) return outerDiv.current.clientWidth;
    return reload;
  }, [reload]);

  const calcEffectiveStyle = useCallback(() => {
    setReload(1);
  }, []);
  const w = useMemo(() => {
    if (typeof width === "string" && width.indexOf("%") !== -1) {
      return divWidth;
    }
    let wi = parseInt(width.toString(), 10);
    if (Number.isNaN(wi)) {
      wi = 1920;
    }
    return wi;
  }, [divWidth, width]);
  const isForcedSquare = useMemo(
    () => ((Number.isFinite(forceAspectRatio) && forceAspectRatio === 1) || round || (className?.includes("rounded-full") ?? false)),
    [forceAspectRatio, className, round]
  );
  const forcedObjectPosition = useMemo(() => {
    if (isForcedSquare && image) {
      const hasCropFocus = image.cropX !== undefined || image.cropY !== undefined;
      if (hasCropFocus) {
        const x = typeof image.cropX === "number" ? Math.min(100, Math.max(0, image.cropX * 100)) : 50;
        const y = typeof image.cropY === "number" ? Math.min(100, Math.max(0, image.cropY * 100)) : 50;
        return `${x}% ${y}%`;
      }
    }
    return "50% 50%";
  }, [isForcedSquare, image]);
  const src = useMemo(() => {
    if (!image) {
      if (imageUrl) {
        return `${imageUrl.replace("/public", "")}/w=${cloudflareWidth}`;
      }
      return "";
    }
    if (!image.url) {
      return "";
    }
    if (image.url.indexOf("public") === -1) return image.url;
    return `${image.url.replace("/public", "")}/w=${cloudflareWidth}`;
  }, [image, cloudflareWidth, imageUrl]);
  const containerStyle = useMemo<React.CSSProperties>(() => {
    if (isForcedSquare) {
      return {
        width,
        height: width,
        overflow: "hidden",
        position: "relative",
        backgroundColor: objectFit === "contain" ? "transparent" : undefined,
        ...(rounded ? { borderRadius: 8 } : {}),
        ...(round ? { borderRadius: 1000 } : {}),
      };
    }
    if (image && image.cropAspectRatio) {
      return {
        width,
        aspectRatio: image.cropAspectRatio,
        overflow: "hidden",
        position: "relative",
        ...(rounded ? { borderRadius: 8 } : {}),
        ...(round ? { borderRadius: 1000 } : {}),
      };
    }
    if (image && (image.cropX !== undefined || image.cropY !== undefined || image.cropScale !== undefined)) {
      return {
        width,
        position: "relative",
        overflow: "hidden",
        ...(rounded ? { borderRadius: 8 } : {}),
        ...(round ? { borderRadius: 1000 } : {}),
      };
    }
    const explicitAspect = Number.isFinite(forceAspectRatio) ? forceAspectRatio : undefined;
    if (explicitAspect !== undefined) {
      return {
        width,
        aspectRatio: explicitAspect,
        ...(rounded ? { borderRadius: 8 } : {}),
        ...(round ? { borderRadius: 1000 } : {}),
      };
    }
    return {
      width,
      ...(rounded ? { borderRadius: 8 } : {}),
      ...(round ? { borderRadius: 1000 } : {}),
    };
  }, [image, width, forceAspectRatio, isForcedSquare, rounded, round, objectFit]);
  const hasCrop = useMemo(() => {
    if (!image) return false;
    return (
      image.cropAspectRatio !== undefined ||
      image.cropScale !== undefined ||
      image.cropX !== undefined ||
      image.cropY !== undefined
    );
  }, [image]);

  const effectiveStyle = useMemo<React.CSSProperties>(() => {
    if (image && hasCrop) {
      return calcMediaStyle(w, image.aspectRatio, image);
    }
    return {};
  }, [w, image, hasCrop]);

  if ((!image || !image.url) && !imageUrl) {
    return null;
  }
  const imgEl = (
    <img
        src={src}
        onLoad={calcEffectiveStyle}
        style={{
          ...(hasCrop
            ? {}
            : {
                width: "100%",
                height: "100%",
                objectFit: objectFit ?? ((isForcedSquare || !!image) ? "cover" : "contain"),
                objectPosition: forcedObjectPosition,
              }),
          display: "block",
          ...effectiveStyle,
        }}
        key={`image-${src}`}
        className={`img ${isEditable ? "editable" : ""}`}
        alt={alt ?? "Thumb"}
    />
  );

  return (
    <div style={containerStyle} className={`${className ?? ""}`} ref={outerDiv}>
      {imgEl}
    </div>
  );
};

export default ClavaImage;
